/**
 * 竞价详情（实时出价面板）
 * IR-01 出价校验 ≥ 当前价 + 加价；IR-02 防秒杀延长 2 分钟高亮；
 * IR-03 断线重连本地缓存出价；IR-04 出价被超越 / 结束通知
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAuction, getAuctionStatus, placeBid } from '@/api/auction';
import { useAuth } from '@/stores/auth';
import type { Auction, AuctionBid } from '@/types';
import { QUICK_BIDS, cosUrl, imgFallback } from '@/utils/constants';
import { formatDateTime, formatMoney, timeAgo } from '@/utils/format';
import Countdown from '@/components/common/Countdown';
import StatusTag from '@/components/common/StatusTag';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import '@/styles/pages/auction.css';

const PENDING_BID_KEY = (id: number): string => `yunniuji_pending_bid_${id}`;

export default function AuctionDetail() {
  const { id } = useParams<{ id: string }>();
  const auctionId = Number(id);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bidInput, setBidInput] = useState('');
  const [myStatus, setMyStatus] = useState<'leading' | 'outbid' | null>(null);
  const [extendedTip, setExtendedTip] = useState(false);
  const [bidding, setBidding] = useState(false);
  const [bidError, setBidError] = useState('');
  const [notice, setNotice] = useState('');
  const pollRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getAuction(auctionId);
      setAuction(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  const pollStatus = useCallback(async () => {
    try {
      const s = await getAuctionStatus(auctionId);
      setAuction((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          current_price: s.current_price,
          end_time: s.end_time,
          bid_count: s.bid_count,
          status: s.status,
          my_bid_status: s.my_bid_status ?? prev.my_bid_status,
        };
      });
      setMyStatus(s.my_bid_status ?? null);
      // IR-02：检测到延长 → 高亮提示
      if (s.extended) {
        setExtendedTip(true);
        window.setTimeout(() => setExtendedTip(false), 5000);
      }
      // IR-04：出价被超越
      if (s.my_bid_status === 'outbid') {
        setNotice('您的出价已被超越，请及时跟进');
      } else if (s.my_bid_status === 'leading') {
        setNotice('');
      }
    } catch {
      /* 轮询失败静默，下一次继续 */
    }
  }, [auctionId]);

  useEffect(() => {
    load();
  }, [load]);

  // 每 10s 轮询状态（防秒杀延长刷新）
  useEffect(() => {
    pollRef.current = window.setInterval(pollStatus, 10000);
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, [pollStatus]);

  // IR-03：断线重连本地缓存出价，恢复后自动同步
  useEffect(() => {
    if (!isAuthenticated) return;
    const cached = localStorage.getItem(PENDING_BID_KEY(auctionId));
    if (!cached) return;
    try {
      const { price } = JSON.parse(cached) as { price: number };
      localStorage.removeItem(PENDING_BID_KEY(auctionId));
      placeBid(auctionId, price)
        .then(() => {
          setNotice('网络已恢复，离线出价已自动同步');
          pollStatus();
        })
        .catch(() => {
          /* 出价可能已失效，忽略 */
        });
    } catch {
      localStorage.removeItem(PENDING_BID_KEY(auctionId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionId, isAuthenticated]);

  const minNextBid = auction ? auction.current_price + auction.bid_increment : 0;

  const handleBid = async (price?: number) => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/auction/${auctionId}`)}`);
      return;
    }
    if (!auction || auction.status !== 1) {
      setBidError('竞价已结束');
      return;
    }
    const finalPrice = price ?? Number(bidInput);
    if (Number.isNaN(finalPrice) || finalPrice < minNextBid) {
      setBidError(`出价必须 ≥ 当前价 + 最小加价（${formatMoney(minNextBid)}）`);
      return;
    }
    setBidding(true);
    setBidError('');
    // IR-03：网络断开时本地缓存最后出价（模拟）
    try {
      localStorage.setItem(PENDING_BID_KEY(auctionId), JSON.stringify({ price: finalPrice }));
    } catch {
      /* ignore */
    }
    try {
      const res = await placeBid(auctionId, finalPrice);
      localStorage.removeItem(PENDING_BID_KEY(auctionId));
      setAuction((prev) =>
        prev
          ? {
              ...prev,
              current_price: res.current_price,
              end_time: res.end_time,
              bid_count: (prev.bid_count ?? 0) + 1,
              bids: prev.bids ? [res.bid, ...prev.bids] : [res.bid],
              my_bid_status: 'leading',
            }
          : prev,
      );
      setMyStatus('leading');
      setNotice('');
      setBidInput('');
      if (res.extended) {
        setExtendedTip(true);
        window.setTimeout(() => setExtendedTip(false), 5000);
      }
    } catch (e) {
      localStorage.removeItem(PENDING_BID_KEY(auctionId));
      setBidError(e instanceof Error ? e.message : '出价失败');
    } finally {
      setBidding(false);
    }
  };

  if (loading && !auction) {
    return (
      <div className="container auction-room">
        <Loading rows={5} />
      </div>
    );
  }

  if (error && !auction) {
    return (
      <div className="container auction-room">
        <ErrorBox message={error} onRetry={load} />
      </div>
    );
  }

  if (!auction) return null;

  const live = auction.status === 1;
  const ended = auction.status === 2 || auction.status === 3 || auction.status === 4;
  const highest = auction.bids?.[0] ?? null;
  const iAmWinner = auction.status === 2 && auction.winner_id === user?.id;

  return (
    <div className="auction-room container">
      {/* 顶部倒计时条 */}
      <div className={`auction-countdown-bar ${live ? 'live' : ''}`}>
        <span className="auction-countdown-bar__label">
          {live ? '⏳ 距结束' : ended ? '已结束' : '预告'}
        </span>
        {live ? <Countdown endTime={auction.end_time} tone="gold" onExpire={pollStatus} /> : null}
        {!live && auction.end_time ? (
          <span className="auction-countdown-bar__time">{formatDateTime(auction.end_time)}</span>
        ) : null}
        {extendedTip ? (
          <span className="auction-extend-tip">⚡ 有新出价，时间延长2分钟（IR-02）</span>
        ) : null}
      </div>

      {notice ? <div className="auction-notice">{notice}</div> : null}

      <div className="auction-room__grid">
        {/* 左：牛只信息 + 价格 */}
        <div className="auction-info">
          <div className="auction-info__cattle">
            <img
              src={auction.cattle?.images?.[0] ?? auction.cattle?.face_image_url ?? cosUrl('/images/cattle-default.jpg')}
              alt={auction.cattle?.breed}
              data-local="/images/cattle-default.jpg"
              onError={imgFallback}
            />
            <div className="auction-info__cattle-body">
              <div className="auction-info__tags">
                <StatusTag type="auction" status={auction.status} />
                <StatusTag type="trade" status={2} />
              </div>
              <h1>{auction.cattle?.breed} · {auction.cattle?.ear_tag_no}</h1>
              <p>
                {auction.cattle?.weight}kg · {auction.cattle?.age_days} 日龄 ·{' '}
                {auction.cattle?.farm_name}
              </p>
              <Link className="auction-info__link" to={`/cattle/${auction.cattle_id}`}>查看完整档案 →</Link>
            </div>
          </div>

          <div className="auction-price-card">
            <div className="auction-price-card__item">
              <span>起拍价</span>
              <b>{formatMoney(auction.base_price)}</b>
            </div>
            <div className="auction-price-card__item auction-price-card__item--current">
              <span>当前最高出价</span>
              <b>{formatMoney(auction.current_price)}</b>
            </div>
            <div className="auction-price-card__item">
              <span>最小加价幅度</span>
              <b>{formatMoney(auction.bid_increment)}</b>
            </div>
            <div className="auction-price-card__item">
              <span>出价次数</span>
              <b>{auction.bid_count}</b>
            </div>
            <div className="auction-price-card__item">
              <span>我的状态</span>
              <b>
                {!isAuthenticated ? (
                  '未登录'
                ) : myStatus === 'leading' ? (
                  <em className="leading">领先</em>
                ) : myStatus === 'outbid' ? (
                  <em className="outbid">已被超越</em>
                ) : (
                  '未出价'
                )}
              </b>
            </div>
          </div>
        </div>

        {/* 右：出价记录 + 操作区 */}
        <div className="auction-side">
          <div className="auction-bids">
            <div className="auction-bids__head">
              <strong>出价记录</strong>
              <span>实时刷新</span>
            </div>
            {auction.bids && auction.bids.length > 0 ? (
              <ul className="auction-bids__list">
                {auction.bids.slice(0, 60).map((b: AuctionBid) => {
                  const mine = b.user_id === (user?.id ?? -1);
                  const isHighest = highest && b.id === highest.id;
                  return (
                    <li className={`auction-bid ${mine ? 'mine' : ''} ${isHighest ? 'highest' : ''}`} key={b.id}>
                      <span className="auction-bid__user">{mine ? '我' : b.user_name ?? '竞买人'}</span>
                      <span className="auction-bid__price">{formatMoney(b.price)}</span>
                      <span className="auction-bid__time">{timeAgo(b.created_at)}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="auction-bids__empty">暂无出价，快来抢第一手</div>
            )}
          </div>

          <div className="auction-operate">
            {live ? (
              <>
                <div className="auction-operate__tip">
                  出价需 ≥ 当前价 + 最小加价（{formatMoney(minNextBid)}）
                </div>
                <div className="auction-input">
                  <input
                    className="form-control"
                    type="number"
                    placeholder={`最低 ${minNextBid}`}
                    value={bidInput}
                    onChange={(e) => {
                      setBidInput(e.target.value);
                      setBidError('');
                    }}
                  />
                  <button
                    className="btn btn-gold"
                    disabled={bidding || Number(bidInput) < minNextBid}
                    onClick={() => handleBid()}
                  >
                    {bidding ? '提交中…' : '提交出价'}
                  </button>
                </div>
                <div className="auction-quick">
                  {/* F5：仅展示 ≥ 最小加价幅度的快捷加价，避免出价过低报错 */}
                  {QUICK_BIDS.filter((q) => q >= auction.bid_increment).map((q) => (
                    <button
                      key={q}
                      className="btn btn-outline btn-sm"
                      onClick={() => handleBid(auction.current_price + q)}
                      disabled={bidding}
                    >
                      +{q}
                    </button>
                  ))}
                </div>
                {bidError ? <div className="form-error">{bidError}</div> : null}
                <div className="auction-operate__rule">
                  英式拍卖：加价幅度最小 {formatMoney(auction.bid_increment)}；最后 1 分钟有新出价自动延长 2 分钟
                </div>
              </>
            ) : auction.status === 2 ? (
              <div className="auction-result">
                <div className="auction-result__title">🎉 竞价成交</div>
                <p>成交价：{formatMoney(auction.winner_price ?? auction.current_price)}</p>
                <p>{iAmWinner ? '恭喜您中标！请下单并完成付款（拍卖订单接入资金托管与电子合同）' : '本场未中标，感谢参与'}</p>
                {iAmWinner ? (
                  <Link
                    className="btn btn-primary"
                    to={`/checkout?cattle_id=${auction.cattle_id}&order_type=3&auction_id=${auctionId}&deal_price=${auction.winner_price ?? auction.current_price}`}
                  >
                    去下单（成交价 {formatMoney(auction.winner_price ?? auction.current_price)}）
                  </Link>
                ) : null}
              </div>
            ) : auction.status === 3 ? (
              <div className="auction-result">
                <div className="auction-result__title">本场流拍</div>
                <p>未达到成交条件，牛只已释放</p>
              </div>
            ) : (
              <div className="auction-result">
                <div className="auction-result__title">竞价已取消</div>
                <p>如有疑问请联系平台客服</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
